import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, NEVER, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { FragmentId } from '../../../core/records/entities/metadata/metadata.entity';
import { ActionItem, ActionItemStyle } from '../../../elements/components/action-item/entities/action-item.entity';
import { NotificationType } from '../../../elements/components/notification/entities/notification-types.entity';
import { ShNotificationComponent } from '../../../elements/components/notification/notification.component';
import { EllipsisMultiActionItem } from '../../../helpers/actions/ellipsis-multi-action.helper';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import {
    defaultPaginationInfo,
    PaginationInfo
} from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { ShGenericOverviewContainerComponent } from '../../../overviews/generic-overview/containers/generic-overview/generic-overview.container.component';
import { ShGenericListComponent } from '../../../overviews/list-view/containers/generic-list/generic-list.component';
import { SearchBarMode } from '../../../overviews/list-view/entities/filter-bar-mode.entity';
import { HierarchyItem } from '../../entities/hierarchy-item.entity';
import { RecordDetailSandbox } from '../../record-detail.sandbox';
import { HierarchyColumn, HierarchyColumnService } from '../../services/hierarchy-column.service';
import { getShareContext } from '../../../session-storage/helpers/fetch-from-session-storage/session-storage.helper';
import { ActivatedRoute } from '@angular/router';

@Component({
    standalone: true,
    selector: 'sh-record-hierarchy-tab',
    templateUrl: './record-hierarchy-tab-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ShGenericListComponent,
        ShGenericOverviewContainerComponent,
        ShNotificationComponent,
        TranslateModule
    ]
})
export class ShRecordHierarchyTabContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly childHierarchyItemsPagination$: BehaviorSubject<PaginationInfo> =
        new BehaviorSubject<PaginationInfo>(defaultPaginationInfo);

    private pureFragment$: Observable<ApiFragment>;
    private mainFragmentId$: Observable<FragmentId>;

    public readonly loadingError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly isLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(true);

    public readonly columns: HierarchyColumn[] = [HierarchyColumnService.MEDIA, HierarchyColumnService.CURRENT_ITEM];

    public hasHierarchy$: Observable<boolean>;
    public mainHierarchyItem$: Observable<HierarchyItem[]>;
    public mainHierarchyItemActions$: Observable<ActionItem[]>;
    public paginatedChildHierarchyItems$: Observable<PaginatedResults<HierarchyItem>>;
    public childHierarchyItemsActions$: Observable<ActionItem[]>;
    public currentHierarchyItem$: Observable<HierarchyItem>;

    public notificationType: typeof NotificationType = NotificationType;
    public searchBarMode: SearchBarMode = SearchBarMode.NONE;

    record = input.required<ApiFragment>();

    private readonly sandbox: RecordDetailSandbox = inject(RecordDetailSandbox);
    private readonly route: ActivatedRoute = inject(ActivatedRoute);

    constructor(public hierarchyColumnService: HierarchyColumnService) {
        super();
    }

    ngOnInit(): void {
        this.initStateStreams();
        this.initSourceStreams();
        this.watchLoadingState();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.loadingError$.complete();
        this.isLoading$.complete();
    }

    public onChildHierarchyItemsPaginationInfoChanged(pagination: PaginationInfo): void {
        this.childHierarchyItemsPagination$.next(pagination);
    }

    private initStateStreams(): void {
        this.pureFragment$ = this.createPureFragmentStream();
        this.hasHierarchy$ = this.createHasHierarchyStream();
    }

    private initSourceStreams(): void {
        this.mainFragmentId$ = this.createMainFragmentIdStream();
        this.mainHierarchyItem$ = this.createMainHierarchyItemStream();
        this.mainHierarchyItemActions$ = this.createMainHierarchyItemActionsStream();

        this.paginatedChildHierarchyItems$ = this.createChildHierarchyItemsStream();
        this.childHierarchyItemsActions$ = this.createChildHierarchyItemsActionsStream();

        this.currentHierarchyItem$ = this.createCurrentHierarchyItemStream();
    }

    // #region State
    private createPureFragmentStream(): Observable<ApiFragment> {
        return of(this.record()).pipe(
            filter((fragment: ApiFragment | null): fragment is ApiFragment => !!fragment),
            tap(() => this.isLoading$.startLoading()),
            shareReplay(1)
        );
    }

    private createHasHierarchyStream(): Observable<boolean> {
        return this.pureFragment$.pipe(map(this.isFragmentPartOfHierarchy.bind(this)), shareReplay(1));
    }

    private isFragmentPartOfHierarchy(fragment: ApiFragment): boolean {
        const hasMainFragment: boolean = fragment.getMainFragmentId() !== null;
        const hasChildFragments: boolean = fragment.getChildFragmentsIds().length > 0;

        return hasMainFragment || hasChildFragments;
    }
    // #endregion

    // #region Main Hierarchy Item
    private createMainFragmentIdStream(): Observable<FragmentId> {
        return this.pureFragment$.pipe(
            filter(this.isFragmentPartOfHierarchy.bind(this)),
            map((fragment: ApiFragment) => {
                const mainFragmentId: FragmentId | null = fragment.getMainFragmentId();
                return mainFragmentId ? mainFragmentId : fragment.getFragmentId();
            }),
            shareReplay(1)
        );
    }

    private createMainHierarchyItemStream(): Observable<HierarchyItem[]> {
        return combineLatest([this.pureFragment$, this.mainFragmentId$]).pipe(
            switchMap(([currentFragment, mainFragmentId]: [ApiFragment, FragmentId]) =>
                combineLatest([this.getMainFragmentById(currentFragment, mainFragmentId), of(currentFragment)])
            ),
            map(([mainFragment, currentFragment]: [ApiFragment, ApiFragment]) => [
                this.recordToHierarchyItem(mainFragment, currentFragment)
            ]),
            catchError(() => this.setLoadingError()),
            shareReplay(1)
        );
    }

    private getMainFragmentById(currentFragment: ApiFragment, mainFragmentId: FragmentId): Observable<ApiFragment> {
        return mainFragmentId === currentFragment.getFragmentId()
            ? of(currentFragment)
            : this.sandbox.getRecord(mainFragmentId);
    }

    private createMainHierarchyItemActionsStream(): Observable<ActionItem[]> {
        return combineLatest([this.mainHierarchyItem$, getShareContext(this.route)]).pipe(
            map(([items, shareContext]: [HierarchyItem[], string | undefined]) =>
                items.map(item => this.getHierarchyItemAction(item, shareContext))
            ),
            shareReplay(1)
        );
    }
    // #region

    // #region Child Hierarchy Items
    private createChildHierarchyItemsStream(): Observable<PaginatedResults<HierarchyItem>> {
        return combineLatest([this.pureFragment$, this.mainFragmentId$]).pipe(
            switchMap(([currentFragment, mainFragmentId]: [ApiFragment, FragmentId]) =>
                combineLatest([this.getChildFragmentsFromMainFragmentId(mainFragmentId), of(currentFragment)])
            ),
            map(([childFragments, currentFragment]: [PaginatedResults<ApiFragment>, ApiFragment]) => {
                return {
                    ...childFragments,
                    results: childFragments.results.map((childFragment: ApiFragment) => {
                        return this.recordToHierarchyItem(childFragment, currentFragment);
                    })
                };
            }),
            catchError(() => this.setLoadingError()),
            shareReplay(1)
        );
    }

    private getChildFragmentsFromMainFragmentId(mainFragmentId: FragmentId): Observable<PaginatedResults<ApiFragment>> {
        return this.childHierarchyItemsPagination$.pipe(
            switchMap((paginationInfo: PaginationInfo) => {
                return this.sandbox.getMainFragmentChildRecords(mainFragmentId, paginationInfo);
            })
        );
    }

    private createChildHierarchyItemsActionsStream(): Observable<ActionItem[]> {
        return combineLatest([this.paginatedChildHierarchyItems$, getShareContext(this.route)]).pipe(
            map(([paginatedResult, shareContext]: [PaginatedResults<HierarchyItem>, string | undefined]) =>
                paginatedResult.results.map(item => this.getHierarchyItemAction(item, shareContext))
            ),
            shareReplay(1)
        );
    }
    // #endregion

    // #region Current Item
    private createCurrentHierarchyItemStream(): Observable<HierarchyItem> {
        return this.pureFragment$.pipe(
            map(
                currentFragment =>
                    new HierarchyItem({
                        fragment: currentFragment,
                        isCurrent: true
                    })
            )
        );
    }
    // #endregion

    // #region Generic Helpers
    private recordToHierarchyItem(fragment: ApiFragment, currentFragment: ApiFragment): HierarchyItem {
        return new HierarchyItem({
            fragment,
            isCurrent: fragment.getFragmentId() === currentFragment.getFragmentId()
        });
    }

    private getHierarchyItemAction(item: HierarchyItem, shareContext?: string): ActionItem {
        const view: ActionItem = {
            label: this.translateService.instant('actions.watch'),
            navigationLink: {
                route: shareContext ? item.fragment.getShareDetailUrl(shareContext) : item.fragment.getDetailUrl()
            }
        };

        return EllipsisMultiActionItem([view], false, ActionItemStyle.PureIconButton);
    }
    // #endregion

    // #region loading
    private setLoadingError(): Observable<never> {
        this.loadingError$.next(true);
        this.isLoading$.stopLoading();

        return NEVER;
    }

    private watchLoadingState(): void {
        this.hasHierarchy$.pipe(takeUntil(this.destroy$)).subscribe((hasHierarchy: boolean) => {
            if (!hasHierarchy) {
                this.isLoading$.stopLoading();
            }
        });

        combineLatest([this.mainHierarchyItem$, this.paginatedChildHierarchyItems$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([mainFragment, childFragments]: [HierarchyItem[], PaginatedResults<HierarchyItem>]) => {
                if (mainFragment.length > 0 && childFragments.results) {
                    this.isLoading$.stopLoading();
                }
            });
    }
    // #endregion
}
